import { useState } from 'react';
import {
  COLUMN_SEARCH_THRESHOLD, Size, canRemoveColumn, comparatorLabelKey, comparatorTakesValue, criteriaProblems, emptyView,
  isAllRecordsView, statusMembersOf, t, viewNameMissing, viewNameTaken, withFixedFirst, type CriteriaRow, type NewViewDraft,
} from '@bms2/shared';
import { DragList } from '../composites/DragList';
import { Checkbox, IconButton, StarToggle, TextInput } from '../controls';
import { Icon } from '../Icon';
import { Modal } from './Modal';
import { ViewCriteriaRows, type CriteriaAttribute, type CriteriaColumn } from './ViewCriteriaRows';
import styles from './NewViewModal.module.css';

/**
 * The New View Modal (piece 5), as Add New View from the view switcher's
 * mandatory row and as Edit View from the pencil on each of the person's own
 * views (decision 2170). It reads as three bands, the view, Show records where
 * and Columns (decision 2175).
 *
 * The Columns band mounts the drag-and-drop list of Composite and data-display
 * components for Selected Columns, drawn once with a handle, the name and a
 * remove per row, so reordering by keyboard alone works here for the same
 * reason it works there. The picker works in column keys and draws labels; the
 * list's first column is the fixed one (decision 2173).
 */
export const newViewModalDocumentedStates = ['empty', 'withCriteria', 'starred', 'edit'] as const;

/** What the window reads of each column the list offers. */
export type ViewColumn = CriteriaColumn;

export interface NewViewModalProps {
  open: boolean;
  /** Add New View, or Edit View with Delete View in its Footer (decision 2170). */
  mode?: 'add' | 'edit';
  subtitle?: string;
  /** Every column the list offers, in its own order; the first is the fixed one (decision 2173). */
  columns: readonly ViewColumn[];
  /** An Item Table's picker refuses its locked columns; a record list's does not. */
  scope?: 'record' | 'itemTable';
  /** The list's plural, for the band with no condition: *All companies show.* */
  plural?: string;
  /** The person's other views on this list, whose names this one may not take. */
  takenNames?: readonly { id: string; name: string }[];
  /** 🔒 The one line naming the sort and rows per page the view keeps (decision 2174). */
  sortSummary?: string;
  initial?: NewViewDraft;
  /** 🔒 The Items list's attribute types, which its condition rows offer after the columns (decision 8800). */
  attributes?: readonly CriteriaAttribute[];
  /**
   * 🔒 A built-in view's Edit View (decision 2981): the name and the conditions
   * read as text, the columns editable, no default tick and no Delete View.
   * `narrows` says the view reads by the list's own parameters where no
   * condition row names them.
   */
  builtIn?: { narrows: boolean };
  /**
   * 🔒 The window closes only when its write is accepted (decision 2170): a
   * host answers the refusal to read inside the window, or null once accepted.
   */
  onSave: (draft: NewViewDraft) => Promise<string | null> | void;
  /** Edit View's Delete View; answers a refusal, or null once the view is gone. */
  onDelete?: () => Promise<string | null>;
  onCancel: () => void;
  openerId?: string | null;
}

/** An act's words and the column it acts on, read as one accessible name. */
function named(act: string, column: string): string {
  return [act, column].join(' ');
}

export function NewViewModal({
  open, mode = 'add', subtitle, columns, scope = 'record', plural = '', takenNames = [], sortSummary, initial, attributes, builtIn, onSave, onDelete, onCancel, openerId = null,
}: NewViewModalProps) {
  const fixed = columns[0]?.key ?? '';
  const [draft, setDraft] = useState<NewViewDraft>(() => (initial ? { ...initial, selectedColumns: withFixedFirst(initial.selectedColumns, fixed) } : emptyView(fixed)));
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [refusedColumn, setRefusedColumn] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const byKey = new Map(columns.map((column) => [column.key, column]));
  const labelOf = (key: string) => byKey.get(key)?.label ?? key;
  const nameMissing = viewNameMissing(draft.name);
  const nameTaken = viewNameTaken(draft.name, takenNames);
  const problems = criteriaProblems(draft.criteria);
  const searchable = columns.length > COLUMN_SEARCH_THRESHOLD;
  const wanted = filter.trim().toLowerCase();
  const available = columns.filter((column) => !draft.selectedColumns.includes(column.key) && (!searchable || column.label.toLowerCase().includes(wanted)));

  const setCriteria = (next: (rows: readonly CriteriaRow[]) => readonly CriteriaRow[]) => setDraft((d) => ({ ...d, criteria: next(d.criteria) }));

  const save = async () => {
    if (nameMissing || nameTaken || busy) return;
    // 🔒 A blank condition is refused at Save with its row marked (decision 2172), never saved as a view that shows nothing.
    setChecked(true);
    if (Object.keys(problems).length > 0) return;
    setBusy(true);
    setRefusal(null);
    const refused = await onSave(draft);
    setBusy(false);
    if (typeof refused === 'string') setRefusal(refused);
    else if (refused === undefined) onCancel();
  };

  const remove = async () => {
    if (!onDelete) return;
    setBusy(true);
    setRefusal(null);
    const refused = await onDelete();
    setBusy(false);
    if (refused) setRefusal(refused);
  };

  const removeColumn = (column: string) => {
    if (!canRemoveColumn(column, scope, fixed)) {
      setRefusedColumn(column);
      return;
    }
    setRefusedColumn(null);
    setDraft((d) => ({ ...d, selectedColumns: d.selectedColumns.filter((c) => c !== column) }));
  };

  const saveReason = nameMissing ? t('view.nameNeeded') : nameTaken ? t('view.nameTaken') : undefined;
  const actions = [
    ...(mode === 'edit' && onDelete
      ? [{ kind: 'destructiveSecondary' as const, label: t('view.delete'), onAct: () => void remove(), disabled: busy, destructive: { subject: initial?.name ?? draft.name, phrase: t('modal.deletePhrase') } }]
      : []),
    { kind: 'cancel' as const, label: t('modal.cancel'), onAct: onCancel, disabled: busy },
    {
      kind: 'primary' as const,
      label: t('view.save'),
      onAct: () => void save(),
      disabled: nameMissing || nameTaken || busy,
      ...(saveReason === undefined ? {} : { disabledReason: saveReason }),
    },
  ];

  return (
    <Modal
      open={open}
      title={t(mode === 'edit' ? 'view.editTitle' : 'view.title')}
      onCancel={onCancel}
      openerId={openerId}
      width="wide"
      dataModal="new-view"
      actions={actions}
      {...(subtitle === undefined ? {} : { subtitle })}
    >
      <section className={styles.band} data-view-band="view">
        <h3 className={styles.heading}>{t('view.band')}</h3>
        <div className={styles.nameLine}>
          {builtIn ? (
            <div className={styles.name} data-view-name-fixed="true">
              <p className={styles.fixedLabel}>{t('view.name')}</p>
              <p className={styles.fixedValue}>{draft.name}</p>
            </div>
          ) : (
            <div className={styles.name}>
              <TextInput
                size={Size.Large}
                label={t('view.name')}
                value={draft.name}
                onChange={(v) => setDraft((d) => ({ ...d, name: v }))}
                required
                name="viewName"
                error={nameTaken ? t('view.nameTaken') : null}
              />
            </div>
          )}
          <span className={styles.star} data-view-star="true">
            <StarToggle pressed={draft.isFavourite} onChange={(v) => setDraft((d) => ({ ...d, isFavourite: v }))} label={t('view.favourite')} />
            <span aria-hidden="true">{t('view.favourite')}</span>
          </span>
        </div>
        {builtIn ? (
          <p className={styles.hint} data-view-built-in="true">{t('view.builtIn.note')}</p>
        ) : (
          <Checkbox label={t('view.default')} checked={draft.isDefault} onChange={(v) => setDraft((d) => ({ ...d, isDefault: v }))} name="viewIsDefault" />
        )}
      </section>

      <section className={styles.band} data-view-criteria="true" data-view-all={isAllRecordsView(draft.criteria) ? 'true' : 'false'}>
        <h3 className={styles.heading}>{t('view.criteria')}</h3>
        {builtIn ? (
          draft.criteria.length > 0 ? (
            <ul className={styles.conditions} data-view-conditions-fixed="true">
              {draft.criteria.map((row) => (
                <li key={row.id}>{conditionText(row, byKey.get(row.field))}</li>
              ))}
            </ul>
          ) : builtIn.narrows ? (
            <p className={styles.hint} data-view-conditions-fixed="true">{t('view.builtIn.fixed')}</p>
          ) : (
            <p className={styles.hint} data-view-none="true">
              {[t('view.noConditions.lead'), plural.toLowerCase(), t('view.noConditions.tail')].filter(Boolean).join(' ')}
            </p>
          )
        ) : null}
        {builtIn ? null : draft.criteria.length === 0 ? (
          <p className={styles.hint} data-view-none="true">
            {[t('view.noConditions.lead'), plural.toLowerCase(), t('view.noConditions.tail')].filter(Boolean).join(' ')}
          </p>
        ) : null}
        {/* 🔒 The criteria-row builder is drawn once and shared with mail's Filter Rules (decision 0834). */}
        {builtIn ? null : (
        <ViewCriteriaRows rows={draft.criteria} columns={columns} {...(attributes ? { attributes } : {})} checked={checked} onRows={setCriteria} />
        )}
      </section>

      <section className={styles.band} data-view-columns="true">
        <h3 className={styles.heading}>{t('view.columns')}</h3>
        <div className={styles.panels}>
          <div className={styles.panel} data-column-panel="selected">
            <p className={styles.panelHeading}>{t('view.selected')}</p>
            <span />
            <div className={styles.scroller}>
              <DragList
                label={t('view.selected')}
                items={draft.selectedColumns.map((key) => ({ id: key, label: labelOf(key), ...(key === fixed ? { fixed: true } : {}) }))}
                onReorder={(next) => setDraft((d) => ({ ...d, selectedColumns: withFixedFirst(next.map((item) => item.id), fixed) }))}
                trailing={(item) =>
                  item.fixed ? null : (
                    <span data-remove-column={item.id}>
                      <IconButton
                        meaning="remove"
                        label={named(t('view.removeColumn'), item.label)}
                        size={Size.Small}
                        name="remove-column"
                        disabled={!canRemoveColumn(item.id, scope, fixed)}
                        disabledReason={t('view.columnLocked')}
                        onClick={() => removeColumn(item.id)}
                      />
                    </span>
                  )
                }
              />
            </div>
          </div>
          <div className={`${styles.panel} ${styles.available}`} data-column-panel="available">
            <p className={styles.panelHeading}>{t('view.available')}</p>
            {/* 🔒 The search box draws only where the list offers more than eight columns (decision 2173). */}
            {searchable ? <TextInput size={Size.Large} label={t('view.searchColumns')} labelHidden value={filter} onChange={setFilter} type="search" /> : <span />}
            <div className={styles.scroller}>
              <ul className={styles.columnList}>
                {available.map((column) => (
                  <li key={column.key}>
                    <button
                      type="button"
                      className={styles.addColumn}
                      data-add-column={column.key}
                      aria-label={named(t('view.addColumn'), column.label)}
                      onClick={() => setDraft((d) => ({ ...d, selectedColumns: [...d.selectedColumns, column.key] }))}
                    >
                      <span className={styles.plus} aria-hidden="true">
                        <Icon meaning="add" size="inline" />
                      </span>
                      {column.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        {sortSummary ? (
          <p className={styles.summary} data-view-sort="true">
            {sortSummary}
          </p>
        ) : null}
        {refusedColumn ? (
          <p className={styles.hint} data-column-refused={refusedColumn}>
            {refusedColumn === fixed ? t('view.fixedColumn') : t('view.columnLocked')}
          </p>
        ) : null}
      </section>

      {refusal ? (
        <p className={styles.refusal} role="alert" data-view-refusal="true">
          {refusal}
        </p>
      ) : null}
    </Modal>
  );
}

/** 🔒 One condition of a built-in view as text (decision 2981): the column, the comparator and the value in the status table's own label. */
function conditionText(row: CriteriaRow, column: ViewColumn | undefined): string {
  const type = column?.type ?? 'text';
  const members = type === 'status' ? statusMembersOf(column?.statusEntity) : [];
  const value = members.find((member) => member.value === row.value)?.label ?? row.value;
  const comparator = comparatorTakesValue(row.comparator) ? [t(comparatorLabelKey(row.comparator, type)), value] : [t(comparatorLabelKey(row.comparator, type))];
  return [column?.label ?? row.field, ...comparator].join(' ');
}
